import React, { useContext, useEffect, useMemo, useState } from 'react'
import { List, ListItem, ListItemText, Button, Badge } from '@mui/material'
import { isMobile } from '../../Utilities/isMobile'
import { KeyboardArrowRight } from '@mui/icons-material'
import { PATHS } from '../../constants'
import moment from 'moment'
moment.locale('ja')
import { useHistory } from 'react-router-dom'
import { EmploymentsContext } from '../../contexts/EmploymentsContext'
import { changeSelectedStore, SELECTED_STORE_CHANGED_EVENT } from '../../Utilities/authenticationService'

const monthKey = date => {
    const year = date.getFullYear()
    const month = date.getMonth() + 1
    return `${year}-${String(month).length > 1 ? month : '0' + month}`
}

const statusBadge = status => {
    if (status === '締め切り済み') {
        return (
            <Badge
                badgeContent="締め切り済み"
                color="paloBlue"
                sx={{
                    width: '6rem',
                    ml: '-3rem'
                }}
            />
        )
    }
    if (status === '提出済み') {
        return (
            <Badge
                badgeContent="提出済み"
                color="success"
                sx={{
                    width: '4rem',
                    ml: '-1.5rem'
                }}
            />
        )
    }
    return (
        <Badge
            badgeContent="未提出"
            color="error"
            sx={{
                width: '4rem',
                ml: '-2rem'
            }}
        />
    )
}

const storeOpenForMonth = (employment, targetString) =>
    !employment?.store?.closed_months?.includes(targetString)

/** Prefer primary employment when its store is open; otherwise first open store. */
const submitEmploymentForMonth = (userEmployments, primaryEmployment, targetString) => {
    if (!userEmployments?.length) return primaryEmployment
    if (primaryEmployment && storeOpenForMonth(primaryEmployment, targetString)) {
        return primaryEmployment
    }
    return userEmployments.find(emp => storeOpenForMonth(emp, targetString)) || primaryEmployment
}

const ShiftSubmissions = () => {
    const { userEmployments } = useContext(EmploymentsContext)
    const history = useHistory()
    const now = new Date()
    const recentMonths = [
        new Date(now.getFullYear(), now.getMonth()),
        new Date(now.getFullYear(), now.getMonth() + 1),
        new Date(now.getFullYear(), now.getMonth() + 2)
    ]
    const [selectedEmpId, setSelectedEmpId] = useState(() => parseInt(localStorage.getItem('selected_emp'), 10))

    useEffect(() => {
        const handleStoreChanged = e => {
            setSelectedEmpId(parseInt(e.detail?.employmentId ?? localStorage.getItem('selected_emp'), 10))
        }
        window.addEventListener(SELECTED_STORE_CHANGED_EVENT, handleStoreChanged)
        return () => window.removeEventListener(SELECTED_STORE_CHANGED_EVENT, handleStoreChanged)
    }, [])

    const primaryEmployment = useMemo(() => {
        if (!userEmployments?.length) return null
        return userEmployments.find(e => e.id === selectedEmpId) || userEmployments[0]
    }, [userEmployments, selectedEmpId])

    const handleClick = (empId, month) => {
        const stored = localStorage.getItem('frederickSelectedYearMonth')
        const current = stored ? JSON.parse(stored) : null
        if (month !== current) {
            localStorage.setItem('frederickSelectedYearMonth', JSON.stringify(month))
        }
        // Keep selected_emp in sync — ShiftManagements rewrites URLs from localStorage.
        changeSelectedStore(empId)
        history.push(
            PATHS.shiftManagement
                .replace(':employmentId', empId)
                .replace(':calendarYearMonth', month)
                .replace(':tabValue', 'submit')
        )
    }

    if (!primaryEmployment) return null

    return (
        <List sx={{ mx: '1rem' }} data-testid="shift-submissions-person-level">
            {recentMonths.map(e => {
                const formattedDate = moment(e).format('YYYY-MM')
                const month = e.getMonth() + 1
                const targetString = moment(e).format('YYYY-MM')
                const submittedKey = monthKey(e)
                const allStoresClosed = userEmployments.every(emp => !storeOpenForMonth(emp, targetString))
                const submitEmployment = submitEmploymentForMonth(
                    userEmployments,
                    primaryEmployment,
                    targetString
                )
                const isSubmitted = Boolean(primaryEmployment.submitted?.find(s => s == submittedKey))
                const status = allStoresClosed ? '締め切り済み' : isSubmitted ? '提出済み' : '未提出'
                const storeLabel =
                    (userEmployments?.length || 0) > 1
                        ? `全所属店舗（${userEmployments.map(emp => emp.store?.name).filter(Boolean).join('、')}）`
                        : primaryEmployment.store?.name

                return (
                    <ListItem key={formattedDate}>
                        <ListItemText
                            primary={storeLabel}
                            secondary={
                                <>
                                    {`${month}月分`}
                                    {statusBadge(status)}
                                </>
                            }
                        />
                        {isMobile() ? (
                            <KeyboardArrowRight
                                sx={{ ml: 'auto' }}
                                onClick={() => handleClick(submitEmployment.id, formattedDate)}
                            />
                        ) : (
                            <Button
                                sx={{ ml: 'auto' }}
                                variant="outlined"
                                disabled={status === '締め切り済み' || status === '提出済み'}
                                onClick={() => handleClick(submitEmployment.id, formattedDate)}
                            >
                                希望シフトを提出する
                            </Button>
                        )}
                    </ListItem>
                )
            })}
        </List>
    )
}

export default ShiftSubmissions
